import { userApi } from "./api/user";
import { LineToolState } from "./charting_library";
import { TradingViewConfig } from "./config/tv-config";
import { objectFromJson } from "./storage";

export interface DrawState {
    symbol: string;
    lines: TradingView.LineToolState[];
}


export abstract class CacheItem<TState> {
    lastId: number = 0;
    saving: boolean = false;
    loading: boolean = true;
    start?: number;
    constructor() {
    }

    abstract restoreState(state: TState): void;
    abstract loadState(): TState;
}

export abstract class CacheItems<TState, TCacheItem extends CacheItem<TState>> {
    protected storage = new Map<string, TCacheItem>();

    public async peak(key: string) {
        await this.loadAndWait(key, true);
    }
    protected async loadAndWait(key: string, peak: boolean = false): Promise<TCacheItem> {
        let cache = this.storage.get(key);
        if (cache) {
            if (cache.loading) {
                if (peak)
                    return cache;
                return new Promise<TCacheItem>(resolve => waitForLoad(resolve, cache));
            }
            return cache;
        }

        this.storage.set(key, cache = this.createItem(key));
        this.proceedWithLoad(key, cache);

        return new Promise<TCacheItem>(resolve => waitForLoad(resolve, cache));

        function waitForLoad(resolve: (value: TCacheItem) => void, c: TCacheItem) {
            if (!c.loading)
                resolve(c);
            else
                setTimeout(() => waitForLoad(resolve, c), 10);
        }
    }

    protected abstract createItem(key: string): TCacheItem;

    private async proceedWithLoad(key: string, cache: TCacheItem) {
        try {
            const state = await this.loadFromServer(key);
            cache.restoreState(objectFromJson<TState>(state));
            cache.loading = false;
        } catch (e) {
            console.error(e);
            setTimeout(() => this.proceedWithLoad(key, cache), 3000);
        }
    }
    public async save(key: string) {
        let cache = this.storage.get(key);
        if (!cache) {
            this.storage.set(key, cache = this.createItem(key));
            cache.loading = false;
        }
        cache.lastId++;
        console.log(`save(${key})`);
        if (!cache.saving && !cache.loading) {
            cache.saving = true;
            this.saveDelay(key, cache);
        }
    }
    private saveDelay(key: string, cache: CacheItem<TState>) {
        setTimeout(async () => {
            const lid = cache.lastId;
            await this.saveToServer(key, JSON.stringify(cache.loadState()));
            if (lid == cache.lastId)
                cache.saving = false;
            else
                this.saveDelay(key, cache);
        }, 5000);
    }

    protected abstract loadFromServer(key: string): Promise<string>;
    protected abstract saveToServer(key: string, data: string): Promise<void>;

}

export class DrawingCachedItem extends CacheItem<DrawState> {
    public lines: LineToolState[];
    public ids = new Set<string>();
    private tid: string;
    constructor(tid: string) {
        super();
        this.tid = tid;
    }

    restoreState(state: DrawState): void {
        this.lines = [];
        this.ids.clear();
        state?.lines?.forEach(v => {
            if (v) {
                this.lines.push(v);
                this.ids.add(v.id);
            }
        });
    }

    loadState(): DrawState {
        const store: DrawState = {
            symbol: this.tid,
            lines: Array.from(this.lines.values()),
        };
        //console.log(store);
        return store;
    }
    has(id: string) {
        return this.ids.has(id);
    }
    add = (id: string) => this.ids.add(id);

    delete = (id: string) => this.ids.delete(id);
    set(lines: Map<string, LineToolState>) {
        this.lines = [];
        lines?.forEach(v => { if (v) this.lines.push(v); });
    }
}
export class DrawingCacheItems extends CacheItems<DrawState, DrawingCachedItem>{

    protected override createItem = (key: string): DrawingCachedItem => new DrawingCachedItem(key);

    protected override saveToServer = (key: string, data: string): Promise<void> => userApi.saveDrawings(key, data);

    protected override loadFromServer = (key: string): Promise<string> => userApi.loadDrawings(key);

    load = async (tid: string) => await this.loadAndWait(tid);
}

export class CacheStorage {
    private _drawings: DrawingCacheItems = new DrawingCacheItems();
    get drawings() { return this._drawings; }

    preload = (tid: string) => this.drawings.peak(tid);

}
